/* NUR CINEMA — cinematic depth behaviors (July 2026).
   Companion to nur-cinema.css; loads AFTER nuriale-modern.js and follows its
   conventions: bail out entirely for reduced-motion users and old browsers
   (and stand down if reduced motion is enabled mid-session), never hide
   anything already in the viewport (no flash), fire every reveal exactly
   once. Scrubbed touches (hero settle, two-plane depth, reading thread,
   and round 2's coming-soon mat drift, silk-film light pass and
   product-row settle) are batched into a single rAF frame with passive
   listeners. In the theme editor, section re-renders re-initialise via
   shopify:section events. Gate class: html.nur-cinema. */
(function () {
  'use strict';
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.documentElement.classList.add('nur-cinema');

  var docEl = document.documentElement;
  var mqDesk = window.matchMedia('(min-width: 1000px)');
  var mqFine = window.matchMedia('(pointer: fine)');
  var dead = false;

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight * 0.9 && r.bottom > 0;
  }

  /* ------------------------------------------------------------------
     C1. PER-WORD HEADING RISE. Only plain-text headings (no child
     elements), 2–14 words; original text kept in aria-label; headings
     already handled by nuriale-modern reveals or the theme's own
     reveal-on-scroll are left alone.
     ------------------------------------------------------------------ */
  function initWords() {
    var prose = '.shopify-section--image-with-text .image-with-text > .prose > ';
    var selectors = [
      '.section-header.text-center .section-header__heading',
      '.shopify-section--featured-collections .section-stack > .v-stack.text-center > h2',
      prose + 'h1', prose + 'h2', prose + 'h3',
      prose + '.h1', prose + '.h2', prose + '.h3',
      '.empty-state.nur-waitlist h1'
    ];
    var targets = [];

    document.querySelectorAll(selectors.join(', ')).forEach(function (el) {
      if (el.closest('[data-nur-reveal], [reveal-on-scroll], .shopify-section--slideshow')) return;
      if (el.childElementCount > 0 || el.classList.contains('nur-words')) return;
      if (onScreen(el)) return; // already visible: leave it perfectly still
      var text = (el.textContent || '').trim().replace(/\s+/g, ' ');
      if (!text || text.length > 90) return;
      var words = text.split(' ');
      if (words.length < 2 || words.length > 14) return;

      el.setAttribute('aria-label', text);
      el.textContent = '';
      words.forEach(function (word, i) {
        var span = document.createElement('span');
        span.className = 'nur-word';
        span.textContent = word;
        span.setAttribute('aria-hidden', 'true');
        span.style.setProperty('--nur-w', Math.min(i, 8));
        el.appendChild(span);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      el.classList.add('nur-words');
      targets.push(el);
    });

    if (!targets.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('nur-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35, rootMargin: '0px 0px -10% 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------
     C2 (still unmask) was removed: every image-with-text still on this
     store carries the theme's own reveal-on-scroll entrance (theme.js
     slides + fades the img), so a second reveal would fight it. Those
     stills keep the native entrance + nuriale-modern's §13 light pass.
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     C3–C5 + C7–C9. SCRUBBED LAYER — one rAF-batched frame handles the
     hero settle, the two-plane editorial depth, the PDP/PLP reading
     thread and, since round 2, the coming-soon mat drift, the silk-film
     light pass and the product-row settle. All reads happen before all
     writes; listeners are passive; nothing runs while the tracked
     elements are off screen.
     ------------------------------------------------------------------ */
  var hero = null;
  var planes = [];
  var thread = null;
  /* Round 2 — scroll choreography (C7–C9): coming-soon section wrapper,
     silk-film screening frames, product-list rows. Same lifecycle as the
     round-1 scrubbed elements: (re)collected in initScrubbed, read+written
     in the single frame(), released by the reduced-motion stand-down. */
  var csWrap = null;
  var films = [];
  var lists = [];

  function initScrubbed() {
    hero = document.querySelector('.shopify-section--slideshow');
    planes = Array.prototype.slice.call(
      document.querySelectorAll('.shopify-section--image-with-text')
    );
    if (thread && thread.parentNode) thread.parentNode.removeChild(thread);
    thread = null;
    if (document.querySelector('.shopify-section--main-product, .shopify-section--main-collection')) {
      thread = document.createElement('div');
      thread.className = 'nur-thread';
      thread.setAttribute('aria-hidden', 'true');
      document.body.appendChild(thread);
    }
    csWrap = document.querySelector('.shopify-section--nur-coming-soon');
    films = Array.prototype.slice.call(document.querySelectorAll('.nur-film__frame'));
    lists = Array.prototype.slice.call(document.querySelectorAll('.product-list'));
  }

  function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }

  var ticking = false;
  function frame() {
    ticking = false;
    var vh = window.innerHeight;
    var desk = mqDesk.matches;

    // -- reads --
    var heroRect = (hero && desk) ? hero.getBoundingClientRect() : null;
    var planeRects = null;
    if (desk && mqFine.matches && planes.length) {
      planeRects = planes.map(function (el) { return el.getBoundingClientRect(); });
    }
    var csRect = (csWrap && desk && mqFine.matches) ? csWrap.getBoundingClientRect() : null;
    var filmRects = null;
    if (desk && mqFine.matches && films.length) {
      filmRects = films.map(function (el) { return el.getBoundingClientRect(); });
    }
    var listRects = null;
    if (desk && mqFine.matches && lists.length) {
      listRects = lists.map(function (el) { return el.getBoundingClientRect(); });
    }
    var scrollY = window.scrollY;
    var scrollable = docEl.scrollHeight - vh;

    // -- writes --
    if (heroRect && heroRect.height > 0 && heroRect.bottom > 0) {
      var hp = clamp(-heroRect.top / heroRect.height, 0, 1);
      hero.style.setProperty('--nur-hero-y', (hp * heroRect.height * 0.18).toFixed(1) + 'px');
      hero.style.setProperty('--nur-hero-o', clamp(1 - hp * 1.15, 0, 1).toFixed(3));
    }

    if (planeRects) {
      planeRects.forEach(function (r, i) {
        if (r.bottom < -80 || r.top > vh + 80 || r.height === 0) return;
        var par = clamp(((r.top + r.height / 2) - vh / 2) / vh, -1, 1);
        planes[i].style.setProperty('--nur-par', par.toFixed(3));
      });
    }

    if (thread) {
      var rp = scrollable > 0 ? clamp(scrollY / scrollable, 0, 1) : 0;
      thread.style.setProperty('--nur-read', rp.toFixed(4));
      thread.style.setProperty('--nur-thread-o', rp > 0.02 ? '1' : '0');
    }

    // C7: coming-soon mats — one signed progress var; CSS maps it to the
    // two differential drift rates (7px panel / 4px carré).
    if (csRect && csRect.height > 0 && csRect.bottom > -80 && csRect.top < vh + 80) {
      var cp = clamp(((csRect.top + csRect.height / 2) - vh / 2) / vh, -1, 1);
      csWrap.style.setProperty('--nur-cs-par', cp.toFixed(3));
    }

    // C8: silk-film light pass — passage 0→1 while the frame crosses the
    // viewport drives the sheen's x (-120% → 120%) and its glow, which
    // peaks mid-passage and is fully out before the frame settles.
    if (filmRects) {
      filmRects.forEach(function (r, i) {
        if (r.height === 0 || r.bottom < 0 || r.top > vh) return;
        var fp = clamp((vh - r.top) / (vh + r.height), 0, 1);
        films[i].style.setProperty('--nur-film-x', (fp * 240 - 120).toFixed(1) + '%');
        films[i].style.setProperty('--nur-film-o', clamp(1 - Math.abs(fp * 2 - 1) * 1.6, 0, 1).toFixed(3));
      });
    }

    // C9: product-row settle — 1→0 (eased) as the row rises through the
    // lower half of the viewport; exactly 0 once home, so settled rows
    // carry a zero offset. One var per row, on the list element.
    if (listRects) {
      listRects.forEach(function (r, i) {
        if (r.height === 0 || r.bottom < 0 || r.top > vh + 60) return;
        var ls = clamp((vh - r.top) / (vh * 0.5), 0, 1);
        lists[i].style.setProperty('--nur-row', ((1 - ls) * (1 - ls)).toFixed(3));
      });
    }
  }

  function requestFrame() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(frame);
    }
  }

  function watch() {
    if (!(hero || planes.length || thread || csWrap || films.length || lists.length)) return;
    // addEventListener dedupes identical listener/capture pairs, so watch()
    // is safe to call again after editor re-inits.
    window.addEventListener('scroll', requestFrame, { passive: true });
    window.addEventListener('resize', requestFrame, { passive: true });
    requestFrame();
  }

  function boot() {
    initWords();
    initScrubbed();
    watch();
  }

  /* If the user enables reduced motion mid-session, stand down entirely:
     drop the gate class (all gated CSS goes inert/static), remove the
     reading thread and stop scrubbing. One-way — a reload re-evaluates. */
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mqReduce.addEventListener) {
    mqReduce.addEventListener('change', function (e) {
      if (!e.matches || dead) return;
      dead = true;
      docEl.classList.remove('nur-cinema');
      if (thread && thread.parentNode) thread.parentNode.removeChild(thread);
      thread = null;
      hero = null;
      planes = [];
      /* Round 2 cleanup: dropping html.nur-cinema already inerts every
         C7–C9 rule (their transforms/overlays live behind the gate);
         release the references so no further vars are written. */
      csWrap = null;
      films = [];
      lists = [];
      window.removeEventListener('scroll', requestFrame);
      window.removeEventListener('resize', requestFrame);
    });
  }

  /* Theme editor: re-rendered sections lose their word spans and orphan the
     scrubbed-element references — re-initialise on section lifecycle events.
     Storefront never fires these. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      if (dead) return;
      initWords();
      initScrubbed();
      watch();
    });
    document.addEventListener('shopify:section:unload', function () {
      if (dead) return;
      initScrubbed();
      watch();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
